import React from 'react';
import { Box, Container } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { IconPaper } from '@bizapp-lp/pjr/molecules/IconPaper';
import { StyledInput } from '@bizapp-lp/pjr/molecules/StyledInput';
import { StyledSelect } from '@bizapp-lp/pjr/molecules/StyledSelect';
import { FormSubtitle } from '@bizapp-lp/pjr/molecules/FormSubtitle';

import { ZipcodeInput } from '@bizapp-lp/pjr/molecules/ZipcodeInput';
import { StyledRadioGroup } from '@bizapp-lp/pjr/molecules/StyledRadioGroup';
import { FileUploader } from '@bizapp-lp/pjr/molecules/FileUploader';
import {
  InputComponentProps,
  SizeType,
} from '@bizapp-lp/pjr/organisms/InputFormType';

export interface TwoColumnFormComponentProps {
  className?: string;
  description?: React.ReactNode;
  inputList?: InputComponentProps[];
  children?: React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: '464px',
      [theme.breakpoints.up('md')]: {
        maxWidth: '888px',
      },
    },
    paperContents: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(4),
        paddingLeft: theme.spacing(4),
      },
      '& .MuiTypography-subtitle1': {
        fontSize: '17px',
        lineHeight: '31px',
        fontWeight: 'bold',
      },
      '& .MuiTypography-body2': {
        marginTop: theme.spacing(0),
        color: '#7F7F7F',
        fontSize: '12px',
      },
    },

    inputFlexbox: {
      display: 'flex',
      flexFlow: 'row wrap',
      marginRight: '-12px',
      marginLeft: theme.spacing(-1.5),
    },
    sectionTitle: {
      margin: theme.spacing(4, 1.5, 0, 1.5),
      '&:first-child': { marginTop: 0 },
      width: '100%',
    },
    inputCommon: {
      margin: theme.spacing(2.25, 1.5, 0, 1.5),
      '&:first-child': { marginTop: 0 },
      width: '100%',
    },
    inputShort: {
      maxWidth: '180px',
      [theme.breakpoints.up('sm')]: {
        maxWidth: '188px',
      },
    },
    inputMedium: {
      [theme.breakpoints.up('md')]: {
        maxWidth: '400px',
      },
    },
    inputLongArea: {
      [theme.breakpoints.up('md')]: {
        maxWidth: '824px',
        '& .MuiInputBase-root': {
          maxWidth: '400px',
        },
        '& .MuiBox-root': {
          maxWidth: '400px',
        },
      },
    },
    inputLong: {
      maxWidth: '824px',
    },
    textAlignRight: {
      '& input': {
        textAlign: 'right',
      },
    },

    getIcon: {
      height: '14px',
      width: '14px',
      color: '#7F7F7F',
    },
  }),
);

export const TwoColumnFormComponent: React.FC<TwoColumnFormComponentProps> = ({
  className = '',
  description,
  inputList = [],
  children,
}) => {
  const classes = useStyles();

  const size2className = (size: SizeType | undefined) => {
    if (size === 'short') {
      return classes.inputShort;
    } else if (size === 'long-area') {
      return classes.inputLongArea;
    } else if (size === 'long') {
      return classes.inputLong;
    }

    return classes.inputMedium;
  };

  const extendInputList = (inputs: InputComponentProps[]) => {
    return (
      <Box className={classes.inputFlexbox}>
        {inputs.map(
          ({
            kind,
            style = {},
            onChange = () => {},

            warning = false,
            onBlur = () => {},
            selectOptions,
            radioOptions,
            valueFile = null,
            onChangeFile,
            acceptMIMETypes = [],
            onSelectZipcode,
            ...props
          }) => {
            const { size, textAlign } = style;
            const className = `${classes.inputCommon} ${size2className(size)} ${
              textAlign === 'right' ? classes.textAlignRight : ''
            }`;

            switch (kind) {
              case 'sectionTitle':
                return (
                  <FormSubtitle
                    {...props}
                    className={classes.sectionTitle}
                    key={props.id}
                  />
                );
              case 'select':
                return (
                  <StyledSelect
                    {...props}
                    options={selectOptions ?? new Map()}
                    onBlur={(ev) => {
                      onBlur(ev.target.value);
                    }}
                    onChange={(ev) => {
                      onChange(ev.target.value as string);
                    }}
                    className={className}
                    key={props.id}
                  />
                );
              case 'zipcode':
                return (
                  <ZipcodeInput
                    {...props}
                    onChange={(ev) => {
                      onChange(ev.target.value);
                    }}
                    onBlur={(ev) => {
                      onBlur(ev.target.value);
                    }}
                    onSelectOption={onSelectZipcode}
                    className={className}
                    key={props.id}
                  />
                );
              case 'radioGroup':
                return (
                  <StyledRadioGroup
                    {...props}
                    options={radioOptions ?? new Map()}
                    onChange={(ev, value) => {
                      onChange(value);
                    }}
                    className={className}
                    key={props.id}
                  />
                );
              case 'fileupload': {
                const { value, ..._props } = props;
                return (
                  <FileUploader
                    {..._props}
                    value={valueFile}
                    onChange={onChangeFile}
                    acceptMIMETypes={acceptMIMETypes}
                    className={className}
                    key={props.id}
                  />
                );
              }
              case 'input': // 'input' and other kind fallthrough into default `StyledInput`
              default:
                return (
                  <StyledInput
                    {...props}
                    warning={warning}
                    onChange={(ev) => {
                      onChange(ev.target.value);
                    }}
                    onBlur={(ev) => {
                      onBlur(ev.target.value);
                    }}
                    className={className}
                    key={props.id}
                  />
                );
            }
          },
        )}
      </Box>
    );
  };

  return (
    <IconPaper
      description={description}
      className={`${classes.paper} ${className}`}
    >
      <Container className={classes.paperContents}>
        {extendInputList(inputList)}
        {children}
      </Container>
    </IconPaper>
  );
};
